import React, { type JSX, type ReactNode } from "react";
import { pickAdminDashboardLocaleText } from "../locale/admin-dashboard-copy";
import { useAdminDashboardLocale } from "../locale/admin-dashboard-locale-context";
import type {
  AdminOperatorExplanationScreen,
  AdminOperatorExplanationSlot,
} from "../locale/admin-operator-explanations";
import { AdminOperatorExplanationBlock } from "./admin-operator-explanation-block";

export type AdminStateMetaItem = {
  key: string;
  label: string;
  value: ReactNode;
};

export type AdminEmptyStateProps = {
  action?: ReactNode;
  body?: string;
  guidance?: {
    screen: AdminOperatorExplanationScreen;
    slot: AdminOperatorExplanationSlot;
  };
  meta?: AdminStateMetaItem[];
  title: string;
  tone?: "empty" | "partial" | "stale";
};

function resolveToneConfig(
  tone: AdminEmptyStateProps["tone"],
  locale: "ko" | "en",
): { background: string; border: string; label: string; textColor: string } {
  if (tone === "stale") {
    return {
      label: pickAdminDashboardLocaleText(locale, { en: "STALE", ko: "지연" }),
      textColor: "#9a3412",
      border: "1px dashed rgba(217, 119, 6, 0.34)",
      background: "rgba(255, 247, 237, 0.88)",
    };
  }

  if (tone === "partial") {
    return {
      label: pickAdminDashboardLocaleText(locale, { en: "PARTIAL", ko: "부분" }),
      textColor: "#0f766e",
      border: "1px dashed rgba(13, 148, 136, 0.28)",
      background: "rgba(240, 253, 250, 0.9)",
    };
  }

  return {
    label: pickAdminDashboardLocaleText(locale, { en: "EMPTY", ko: "비어 있음" }),
    textColor: "#64748b",
    border: "1px dashed rgba(100, 116, 139, 0.34)",
    background: "rgba(248, 250, 252, 0.76)",
  };
}

export function AdminEmptyState(props: AdminEmptyStateProps): JSX.Element {
  const { locale } = useAdminDashboardLocale();
  const tone = resolveToneConfig(props.tone, locale);

  return (
    <section
      style={{
        padding: "24px",
        borderRadius: "20px",
        border: tone.border,
        background: tone.background,
      }}
    >
      <p style={{ margin: 0, color: tone.textColor, fontSize: "0.78rem", fontWeight: 700 }}>
        {tone.label}
      </p>
      <h3 style={{ margin: "8px 0 0", fontSize: "1.1rem", letterSpacing: "-0.02em" }}>{props.title}</h3>
      {props.body ? (
        <p style={{ margin: "8px 0 0", color: "#475569", lineHeight: 1.5 }}>{props.body}</p>
      ) : null}
      {props.guidance ? (
        <AdminOperatorExplanationBlock
          screen={props.guidance.screen}
          slot={props.guidance.slot}
          style={{ marginTop: "14px" }}
        />
      ) : null}
      {props.meta && props.meta.length > 0 ? (
        <dl
          style={{
            margin: "16px 0 0",
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: "12px",
          }}
        >
          {props.meta.map((item) => (
            <div key={item.key}>
              <dt
                style={{
                  margin: 0,
                  color: "#64748b",
                  fontSize: "0.72rem",
                  fontWeight: 700,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                }}
              >
                {item.label}
              </dt>
              <dd style={{ margin: "4px 0 0", color: "#18212b", fontSize: "0.92rem" }}>{item.value}</dd>
            </div>
          ))}
        </dl>
      ) : null}
      {props.action ? <div style={{ marginTop: "16px" }}>{props.action}</div> : null}
    </section>
  );
}
